import React, { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SEO } from "@/components/common/SEO";
import { toast } from "sonner";
import {
  LogOut,
  Download,
  Trash2,
  Plus,
  Edit,
  Save,
  X,
  MessageSquare,
  FileText,
  Settings,
  Lock,
  RefreshCcw,
} from "lucide-react";

const TOKEN_KEY = "orion-admin-token";

// Admin token is kept in sessionStorage (cleared when tab closes) to reduce
// the attack surface vs localStorage persistence. For stronger isolation in
// production, move this to an httpOnly cookie issued by the backend.
const readToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

const writeToken = (t) => {
  try {
    sessionStorage.setItem(TOKEN_KEY, t);
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("admin token sessionStorage write failed", err);
    }
  }
};

const clearToken = () => {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* no-op */
  }
};

const EDITABLE_CONTENT_KEYS = [
  { key: "hero.title", label: "Hero — Headline" },
  { key: "hero.subtitle", label: "Hero — Subheadline" },
  { key: "hero.ctaPrimary", label: "Hero — Primary CTA" },
  { key: "hero.ctaSecondary", label: "Hero — Secondary CTA" },
  { key: "hero.eyebrow", label: "Hero — Eyebrow text" },
  { key: "hero.disclosure", label: "Hero — Legal disclosure" },
  { key: "audit.title", label: "Free Audit — Title" },
  { key: "audit.desc", label: "Free Audit — Description" },
  { key: "trust.sectionTitle", label: "Trust — Section title" },
  { key: "about.story", label: "About — Story paragraph" },
  { key: "contact.address", label: "Contact — Address" },
];

const adminFetch = async (path, token, options = {}) => {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": token,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) throw new Error("unauth");
  return res;
};

// ========== LOGIN ==========
const Login = ({ onSuccess }) => {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminFetch("/admin/verify", token);
      if (res.ok) {
        writeToken(token);
        onSuccess(token);
      } else {
        toast.error("Invalid token");
      }
    } catch {
      toast.error("Invalid token");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-blue-900/5 p-10">
        <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center">
          <Lock className="w-5 h-5" />
        </div>
        <h1 className="mt-6 font-heading text-3xl tracking-tighter font-medium text-slate-900">Admin Console</h1>
        <p className="mt-2 text-sm text-slate-500">Enter the admin token to continue.</p>
        <div className="mt-8 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Admin Token</Label>
          <Input
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="h-12 rounded-lg"
            autoFocus
            required
          />
        </div>
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="btn-primary-orion w-full mt-6 disabled:opacity-60"
        >
          {loading ? "Verifying…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

// ========== LEADS TAB ==========
const LeadsTab = ({ token }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch("/leads?limit=500", token);
      setLeads(await res.json());
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await adminFetch(`/leads/${id}`, token, { method: "DELETE" });
      toast.success("Lead deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = async () => {
    const res = await fetch(`${API}/leads.csv`, { headers: { "X-Admin-Token": token } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "orion-fm-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-leads-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-medium text-slate-900">Leads</h2>
          <p className="text-sm text-slate-500 mt-1">{leads.length} total</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100">
            <RefreshCcw className="w-4 h-4" /> Refresh
          </button>
          <button data-testid="admin-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.1em] text-slate-500">
              <tr>
                <th className="text-start px-4 py-3">Date</th>
                <th className="text-start px-4 py-3">Type</th>
                <th className="text-start px-4 py-3">Name</th>
                <th className="text-start px-4 py-3">Phone</th>
                <th className="text-start px-4 py-3">Email</th>
                <th className="text-start px-4 py-3">Service / Details</th>
                <th className="text-start px-4 py-3">Source</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">No leads yet.</td></tr>
              ) : leads.map((l) => (
                <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                      {l.type || "lead"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{l.name}</td>
                  <td className="px-4 py-3">{l.phone}</td>
                  <td className="px-4 py-3 text-slate-600">{l.email || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[260px]">
                    <div className="truncate">
                      {l.service || l.facility_type || l.message || "—"}
                      {l.estimated_savings ? ` · AED ${l.estimated_savings.toLocaleString()} savings` : ""}
                    </div>
                    {(l.property_type || l.location || l.preferred_date) && (
                      <div className="mt-1 text-[11px] text-slate-400 truncate">
                        {[l.property_type, l.size, l.location, l.preferred_date].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    {l.career_meta && (
                      <div className="mt-1 text-[11px] text-slate-400 truncate">
                        {[
                          l.career_meta.role_applied,
                          l.career_meta.years_experience,
                          l.career_meta.nationality,
                          l.career_meta.visa_status,
                          l.career_meta.cv_link,
                        ].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{l.page_source || "—"}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`admin-lead-delete-${l.id}`}
                      onClick={() => onDelete(l.id)}
                      className="text-slate-400 hover:text-red-600 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ========== BLOG TAB ==========
const emptyPost = {
  slug: "",
  title_en: "",
  title_ar: "",
  excerpt_en: "",
  excerpt_ar: "",
  content_en: "",
  content_ar: "",
  category: "",
  image_url: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200",
  read_time: 5,
  published: true,
};

const BlogTab = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await adminFetch("/blog/all", token);
    setPosts(await res.json());
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.slug || !editing.title_en) return toast.error("Slug and English title required");
    try {
      await adminFetch("/blog/posts", token, { method: "POST", body: JSON.stringify(editing) });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete "${slug}"?`)) return;
    await adminFetch(`/blog/posts/${slug}`, token, { method: "DELETE" });
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-blog-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-medium text-slate-900">Blog posts</h2>
          <p className="text-sm text-slate-500 mt-1">{posts.length} total</p>
        </div>
        <button
          data-testid="admin-blog-new"
          onClick={() => setEditing({ ...emptyPost })}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      {editing && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-medium">{editing.id ? "Edit post" : "New post"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-slate-500" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <Label>Cover Image</Label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                data-testid="blog-image-upload"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)"); return; }
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const r = await fetch(`${API}/admin/uploads/image`, {
                      method: "POST",
                      headers: { "X-Admin-Token": token },
                      body: fd,
                    });
                    if (!r.ok) throw new Error("upload failed");
                    const d = await r.json();
                    setEditing({ ...editing, image_url: d.url });
                    toast.success("Image uploaded");
                  } catch { toast.error("Upload failed"); }
                }}
                className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 file:px-3 file:py-2 file:text-xs file:font-semibold hover:file:bg-blue-100 cursor-pointer"
              />
              {editing.image_url && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={editing.image_url.startsWith("/blog/") ? `${API}${editing.image_url}` : editing.image_url} alt="cover" className="w-20 h-20 rounded-lg object-cover border border-slate-200" />
                  <div className="text-xs text-slate-500 break-all">{editing.image_url}</div>
                </div>
              )}
            </div>
            <div><Label>Read time (min)</Label><Input type="number" value={editing.read_time} onChange={(e) => setEditing({ ...editing, read_time: parseInt(e.target.value || "5") })} /></div>
            <div><Label>Title (EN)</Label><Input value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} /></div>
            <div><Label>Title (AR)</Label><Input value={editing.title_ar} onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Excerpt (EN)</Label><Textarea rows={2} value={editing.excerpt_en} onChange={(e) => setEditing({ ...editing, excerpt_en: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Excerpt (AR)</Label><Textarea rows={2} value={editing.excerpt_ar} onChange={(e) => setEditing({ ...editing, excerpt_ar: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Content (EN)</Label><Textarea rows={6} value={editing.content_en} onChange={(e) => setEditing({ ...editing, content_en: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Content (AR)</Label><Textarea rows={6} value={editing.content_ar} onChange={(e) => setEditing({ ...editing, content_ar: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
              Published
            </label>
          </div>
          <div className="flex gap-2">
            <button data-testid="admin-blog-save" onClick={save} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {loading ? <div className="p-8 text-slate-400 text-center">Loading…</div> : posts.map((p) => (
          <div key={p.slug} className="p-5 flex items-center gap-4">
            <img src={p.image_url?.startsWith("/blog/") ? `${API}${p.image_url}` : p.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">{p.category}</span>
                {!p.published && <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Draft</span>}
              </div>
              <div className="font-heading font-medium text-slate-900 truncate mt-1">{p.title_en}</div>
              <div className="text-xs text-slate-500 mt-0.5">/{p.slug}</div>
            </div>
            <button onClick={() => setEditing({ ...p, id: p.id || "x" })} className="p-2 text-slate-500 hover:text-blue-700"><Edit className="w-4 h-4" /></button>
            <button onClick={() => del(p.slug)} className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ========== CONTENT TAB ==========
const ContentTab = ({ token }) => {
  const [blocks, setBlocks] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/content`);
    const data = await res.json();
    const map = {};
    data.forEach((b) => { map[`${b.key}::${b.lang}`] = b.value; });
    setBlocks(map);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key, lang, value) => {
    try {
      await adminFetch("/content", token, { method: "PUT", body: JSON.stringify({ key, lang, value }) });
      toast.success("Saved");
      setBlocks((b) => ({ ...b, [`${key}::${lang}`]: value }));
      // Let the public site refresh its overrides live
      window.dispatchEvent(new CustomEvent("orion-content-updated"));
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <div data-testid="admin-content-tab">
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-medium text-slate-900">Content blocks</h2>
        <p className="text-sm text-slate-500 mt-1">Override default copy. Empty value falls back to translations.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {loading ? <div className="p-8 text-slate-400 text-center">Loading…</div> : EDITABLE_CONTENT_KEYS.map(({ key, label }) => (
          <ContentRow key={key} keyName={key} label={label} blocks={blocks} onSave={save} />
        ))}
      </div>
    </div>
  );
};

const ContentRow = ({ keyName, label, blocks, onSave }) => {
  const [en, setEn] = useState(blocks[`${keyName}::en`] || "");
  const [ar, setAr] = useState(blocks[`${keyName}::ar`] || "");

  useEffect(() => {
    setEn(blocks[`${keyName}::en`] || "");
    setAr(blocks[`${keyName}::ar`] || "");
  }, [blocks, keyName]);

  return (
    <div className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading font-medium text-slate-900">{label}</div>
          <div className="text-xs text-slate-500 font-mono mt-0.5">{keyName}</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">English</Label>
          <Textarea value={en} rows={2} onChange={(e) => setEn(e.target.value)} className="mt-1" />
          <button onClick={() => onSave(keyName, "en", en)} className="mt-2 text-xs font-semibold text-blue-700 hover:text-blue-900">Save EN</button>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Arabic</Label>
          <Textarea value={ar} rows={2} onChange={(e) => setAr(e.target.value)} className="mt-1" dir="rtl" />
          <button onClick={() => onSave(keyName, "ar", ar)} className="mt-2 text-xs font-semibold text-blue-700 hover:text-blue-900">Save AR</button>
        </div>
      </div>
    </div>
  );
};

// ========== SHELL ==========
export default function Admin() {
  const [token, setToken] = useState(readToken);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    if (!token) return;
    // Quick re-verify to catch expired/wrong tokens
    adminFetch("/admin/verify", token).catch(() => {
      clearToken();
      setToken("");
    });
  }, [token]);

  const logout = () => {
    clearToken();
    setToken("");
  };

  if (!token) return (<><SEO title="Admin · Orion FM" description="Orion FM admin console." /><Login onSuccess={setToken} /></>);

  const tabs = [
    { id: "leads", label: "Leads", Icon: MessageSquare },
    { id: "blog", label: "Blog", Icon: FileText },
    { id: "content", label: "Content", Icon: Settings },
  ];

  return (
    <div data-testid="admin-page" className="min-h-screen bg-slate-50">
      <SEO title="Admin · Orion FM" description="Orion FM admin console." />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center font-heading font-semibold">O</div>
            <div>
              <div className="font-heading font-medium text-slate-900">Orion FM Admin</div>
              <div className="text-xs text-slate-500">Content · Leads · Blog</div>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-100">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex gap-1">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "leads" && <LeadsTab token={token} />}
        {tab === "blog" && <BlogTab token={token} />}
        {tab === "content" && <ContentTab token={token} />}
      </main>
    </div>
  );
}
